﻿<script setup>
    import { ref, computed, watchEffect } from 'vue'
    import { useRouter, useRoute } from 'vue-router'
    import { useAuthStore } from '@/modules/app/auth/authstore';
    import { useLookupStore } from '@/common/lookups/lookupstore';
    import { useCacheStore } from '@/common/cache/cachestore';
    import Settings from '@/common/settings/settings';

    const route = useRoute();
    const router = useRouter();
    const auth = useAuthStore();
    const lookups = useLookupStore();
    const cache = useCacheStore();

    lookups.Update();
    cache.Update();

    //#region Drawer
    const leftDrawerOpen = ref(false)
    function toggleLeftDrawer() {
        leftDrawerOpen.value = !leftDrawerOpen.value
    }
    //#endregion

    //#region Auth
    function logout() {
        auth.set(null);
        router.push('/app/auth/login');
    }

    watch(() => auth.auth, (newVal, oldVal) => {
        if (!newVal) logout();
    });

    if (!auth.auth) {
        if (window.location.hash != '#/app/auth/login') {
            auth.redirect = window.location.hash;
        }
        logout();
    }
    //#endregion

    //#region Settings
    const ShowDeleted = ref(null);
    async function GetShowDeleted() {
        ShowDeleted.value = await Settings.GetShowDeleted();
    }
    async function SetShowDeleted() {
        await Settings.SetShowDeleted(ShowDeleted.value);
        router.go(0);
    }
    watch(ShowDeleted, async (newVal, oldVal) => {
        if (newVal == oldVal || oldVal == null) return;
        await SetShowDeleted();
    });
    GetShowDeleted();
    //#endregion

    function onLogoClick() {
        router.push('/');

    }
</script>

<template>
    <template v-if="auth.auth == null">
        <q-layout>
            <q-page-container>
                <router-view />
            </q-page-container>
        </q-layout>
    </template>
    <template v-if="auth.auth != null">
        <q-layout view="hHh LpR fFf">

            <q-header reveal elevated bg-primary text-white>
                <q-toolbar>
                    <q-btn dense flat round icon="menu" @click="toggleLeftDrawer" />

                    <q-toolbar-title>
                        <a @click="onLogoClick"><img src="/logo_support.png" style="height:25px;" class="mt-[10px]" /></a>
                    </q-toolbar-title>

                    <q-btn round icon="settings" color="warning" size="sm" to="/app/system/system" mr-3 />
                    <q-btn round icon="tune" :color="ShowDeleted ? 'negative' : 'grey'" size="sm" @click="" mr-3 v-inrole:Global|Admin>
                        <q-menu>
                            <q-list style="min-width: 180px">
                                <q-item clickable v-close-popup to="/app/settings/app">
                                    <q-item-section>
                                        <div flex>
                                            <div flex-none w-6>
                                                <q-icon name="settings" size="18px" />
                                            </div>
                                            <div flex-auto>
                                                App Settings
                                            </div>
                                        </div>
                                    </q-item-section>
                                </q-item>
                                <q-item clickable v-close-popup to="/app/lookups/lookups">
                                    <div flex>
                                        <div flex-none w-6>
                                            <q-icon name="list" size="18px" />
                                        </div>
                                        <div flex-auto>
                                            Lookup Lists
                                        </div>
                                    </div>
                                </q-item>
                                <q-item clickable v-close-popup to="/app/auth/users">
                                    <div flex>
                                        <div flex-none w-6>
                                            <q-icon name="security" size="18px" />
                                        </div>
                                        <div flex-auto>
                                            Security
                                        </div>
                                    </div>
                                </q-item>
                                <q-item clickable v-close-popup to="/app/events/events">
                                    <div flex>
                                        <div flex-none w-6>
                                            <q-icon name="bolt" size="18px" />
                                        </div>
                                        <div flex-auto>
                                            Event Log
                                        </div>
                                    </div>
                                </q-item>
                                <q-separator />
                                <q-item>
                                    <div>
                                        <q-toggle dense v-model="ShowDeleted" label="Show Deleted" />
                                    </div>
                                </q-item>
                            </q-list>
                        </q-menu>
                    </q-btn>
                    <q-btn round color="white" text-color="blue" size="sm" :label="auth.user.Initials">
                        <q-menu>
                            <q-list style="min-width: 180px">
                                <q-item clickable v-close-popup to="/app/settings/user">
                                    <div flex>
                                        <div flex-none w-6>
                                            <q-icon name="settings_accessibility" size="18px" />
                                        </div>
                                        <div flex-auto>
                                            User Settings
                                        </div>
                                    </div>
                                </q-item>
                                <q-separator />
                                <q-item clickable v-close-popup @click="logout">
                                    <div flex>
                                        <div flex-none w-6>
                                            <q-icon name="logout" size="18px" />
                                        </div>
                                        <div flex-auto>
                                            Log Out
                                        </div>
                                    </div>
                                </q-item>
                                <q-separator />
                                <q-item>
                                    <q-item-section>
                                        <version />
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-menu>
                    </q-btn>
                </q-toolbar>
            </q-header>

            <q-drawer show-if-above :breakpoint="1439" :width="250" v-model="leftDrawerOpen" side="left">
                <navmenu />
            </q-drawer>

            <q-page-container flex flex-col h-screen>
                <router-view />
            </q-page-container>
        </q-layout>
    </template>
</template>

<style lang="scss">
    @import './variables.scss';

    @font-face {
        font-family: 'Inter-Regular';
        font-style: normal;
        src: local('Inter-Regular'), url(@/assets/Inter-Regular.ttf) format('truetype');
    }

    html {
        font-size: 100%;
    }
    /*16px*/

    body {
        background: white;
        font-family: 'Inter-Regular';
        font-weight: 400;
        line-height: 1.75;
        color: #000000;
        width: 100%;
    }

    a {
        cursor: pointer;
        color: #2653a6;
        text-decoration:none;
    }

    p {
        margin-bottom: .5rem;
        white-space: pre-wrap;
    }

    h1, h2, h3, h4, h5 {
        margin: .5rem;
        font-family: 'Poppins', sans-serif;
        font-weight: 400;
        line-height: 1.3;
    }

    h1, .h1 {
        margin-top: 0;
        font-size: 1.602rem;
    }

    h2, .h2 {
        font-size: 1.424rem;
    }

    h3, .h3 {
        font-size: 1.266rem;
    }

    h4, .h4 {
        font-size: 1.125rem;
    }

    h5, .h6 {
        font-size: 1rem;
    }

    small, .text_small {
        font-size: 0.889rem;
    }

    ul {
        padding:1rem;
        margin:0;
    }

    .menu-active {
        color: $primary;
        font-weight: bold;
    }

    .isdeleted {
        background-color: $deleted !important;
    }

    .q-table {
        thead tr th {
            font-weight: bold;
            background-color: #fff;
        }
    }
</style>
